import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * v14 guided tours: which walkthroughs and one-time hints this device has
 * seen. Per-device convenience only (never synced); You → Settings can
 * replay them.
 */
export type TourId = 'app' | 'date' | 'hint:reopen';

interface ToursState {
  seen: Partial<Record<TourId, true>>;
  markSeen: (id: TourId) => void;
  /** Show every tour and hint again. */
  reset: () => void;
}

const safeStorage = createJSONStorage(() => {
  try {
    const s = window.localStorage;
    const k = '__popin_probe__';
    s.setItem(k, '1');
    s.removeItem(k);
    return s;
  } catch {
    const mem = new Map<string, string>();
    return {
      getItem: (k: string) => mem.get(k) ?? null,
      setItem: (k: string, v: string) => void mem.set(k, v),
      removeItem: (k: string) => void mem.delete(k),
    };
  }
});

export const useTours = create<ToursState>()(
  persist(
    (set) => ({
      seen: {},
      markSeen: (id) => set((s) => ({ seen: { ...s.seen, [id]: true } })),
      reset: () => set({ seen: {} }),
    }),
    { name: 'popin.tours', storage: safeStorage },
  ),
);
